import type { routes } from '@semios/app-platform-banyan-route-definitions'
import { getTimezoneForSelectedPropertyOrRegion } from 'App/Map/PanelDetails/_utils/getTimezoneForSelectedPropertyOrRegion'
import { selectedPropertyHasPermission } from 'App/Map/PanelDetails/_utils/selectedPropertyHasPermission'
import { DropdownSelectorProperty } from 'App/Map/PanelDetails/SectionTitleBars/DropdownSelectorProperty/DropdownSelectorProperty'
import type { TValuesTimeseriesProperties } from 'App/Map/types'
import type { StackedChartPestSection } from 'components/StackedChart/types'
import { translate } from 'i18n/i18n'
import { detailsPanelStore } from 'stores/detailsPanelStore/detailsPanelStore'
import { fieldAssetStore } from 'stores/fieldAssetStore/fieldAssetStore'
import type { TSelectedFieldAssetsStoreState } from 'stores/selectedFieldAssetsStore/selectedFieldAssetsStore'
import { selectedFieldAssetsStore } from 'stores/selectedFieldAssetsStore/selectedFieldAssetsStore'
import type { selectedValueGroupsStore } from 'stores/selectedValueGroupsStore/selectedValueGroupsStore'
import { doesSelectedPropertyHaveValueTypes } from 'utils/doesSelectedFieldAssetHaveValueTypes'
import { isRegionDataEnabledForCharts } from 'utils/useIsRegionDataEnabled'
import { getDegreeDayValuesRequested } from './_utils/getDegreeDayValuesRequested'
import { getInsectTrapCatchesValuesRequested } from './_utils/getTrapCatchValuesRequested'
import { insectTrapAndDegreeContent } from './_utils/insectTrapAndDegree'

export const apiArgs = ({
  selectedFieldAssets,
  selectedValueGroups,
}: {
  selectedValueGroups: ReturnType<typeof selectedValueGroupsStore.getState>['selectedValueGroups']
  selectedFieldAssets: TSelectedFieldAssetsStoreState
}): Partial<routes.Values.Request> => {
  if (!selectedFieldAssets.property) return {}

  const degreeDaysValuesRequested = getDegreeDayValuesRequested({
    selectedValueGroups,
    doesTargetHaveValueTypes: doesSelectedPropertyHaveValueTypes,
    permissionCheck: (permissionId) => selectedPropertyHasPermission({ permission: permissionId }),
  })

  const trapCatchValuesRequested = getInsectTrapCatchesValuesRequested({
    selectedValueGroups,
    doesTargetHaveValueTypes: doesSelectedPropertyHaveValueTypes,
    permissionCheck: (permissionId) => selectedPropertyHasPermission({ permission: permissionId }),
  })

  const valuesToBeRequested: Partial<routes.Values.Request> = {}

  valuesToBeRequested.properties = {
    propertyIds: [selectedFieldAssets.property],
    valuesRequested: { ...degreeDaysValuesRequested, ...trapCatchValuesRequested },
  }

  return valuesToBeRequested
}

export const buildPropertySection = ({
  data,
  selectedValueGroups,
  compareSeasonsData,
}: {
  data: routes.Values.Response
  compareSeasonsData: routes.Values.Response
  selectedValueGroups: ReturnType<typeof selectedValueGroupsStore.getState>['selectedValueGroups']
}): StackedChartPestSection[] => {
  // builds series for DD
  // builds series for catches
  // if there's a region, builds series for regional DD
  // if there's a region, builds series for regional trap %
  // puts into a chart
  // returns section with header etc

  const insects = fieldAssetStore.getState()?.insects

  if (!insects) return []

  const selectedFieldAssets = selectedFieldAssetsStore.getState()
  const selectedProperty = selectedFieldAssets.property
  const selectedRegion = selectedFieldAssets.region
  const timezone = getTimezoneForSelectedPropertyOrRegion()
  const { dateFrom, dateTo } = detailsPanelStore.getState()
  const regionsData = data?.regions?.[String(selectedRegion)]
  const regionName = fieldAssetStore.getState()?.regions?.[String(selectedRegion)]?.name ?? null
  const targetScopeId = String(selectedFieldAssets.property)
  const showRegionalData = isRegionDataEnabledForCharts()
  const sectionTitleSuffix = translate.phrases.banyanApp('Property')

  type FieldAssetPickerProps = {
    valuesTimeseriesToFilterOn: TValuesTimeseriesProperties[]
  }

  const FieldAssetPicker: React.FC<FieldAssetPickerProps> = ({
    valuesTimeseriesToFilterOn,
  }: FieldAssetPickerProps) => (
    <DropdownSelectorProperty valuesTimeseriesToFilterOn={valuesTimeseriesToFilterOn} />
  )

  return insectTrapAndDegreeContent({
    insects,
    selectedValueGroups,
    targetScope: 'PROPERTY',
    targetScopeId,
    targetScopeData: data?.properties,
    regionName,
    regionsData,
    compareSeasonsTargetScopeData: compareSeasonsData?.properties,
    doesTargetHaveValueTypes: doesSelectedPropertyHaveValueTypes,
    sectionTitleSuffix,
    FieldAssetPicker,
    pestSectionCategory: 'combined-property-level',
    showRegionalData,
    selectedProperty,
    dateFrom,
    dateTo,
    timezone,
  })
}
